import { EditOutlined, FileExcelTwoTone, DownOutlined, CalendarOutlined } from '@ant-design/icons'
import { Button, Card, Col, DatePicker, Input, Row, Select, Dropdown, Modal, Space } from 'antd'
import dayjs from 'dayjs'
import _ from 'lodash'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { departmentService } from '../../common/service'
import { attendanceService } from '../../common/service/AttendanceService'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import PageHeader from '../../common/components/Header/PageHeader'
import { useTheme, useThemeColors } from '../../common/ThemeContext'
import { AntTable } from '../../common/components/Table/AntTable'
import AttendanceEditorPage from './AttendanceEditorPage'
import { DATE_FORMATS } from '../../common/constant'

const { RangePicker } = DatePicker
const PAGE_SIZE = 10

const AttendancePage = () => {
	const theme = useThemeColors()
	const { isDarkMode } = useTheme()
	const { primary } = theme
	const [data, setData] = useState<Array<any>>([])
	const [loading, setLoading] = useState<boolean>(false)
	const [exportLoading, setExportLoading] = useState<boolean>(false)
	const [editAttendanceId, setEditAttendanceId] = useState<string | null>(null)
	const [departmentOptions, setDepartmentOptions] = useState<
		Array<{ label: string; value: string }>
	>([])
	const [cursorPagination, setCursorPagination] = useState({
		current: 1,
		pageSize: PAGE_SIZE,
		total: 0,
		firstDocId: undefined as string | undefined,
		lastDocId: undefined as string | undefined,
		hasNextPage: false,
		hasPrevPage: false,
	})
	const cursorPaginationRef = useRef(cursorPagination)
	cursorPaginationRef.current = cursorPagination
	const [tableState, setTableState] = useState<any>({
		search: '',
		department: undefined,
		startDate: dayjs().format(DATE_FORMATS.DISPLAY),
		endDate: dayjs().format(DATE_FORMATS.DISPLAY),
	})

	const tableHeader = [
		{
			title: 'Action',
			key: 'action',
			render: (item: any) => (
				<Button
					type='link'
					onClick={() => setEditAttendanceId(item?.id)}
					icon={<EditOutlined />}
				/>
			),
		},
		{ title: 'Staff Name (ID)', dataIndex: 'fullName', key: 'fullName' },
		{ title: 'Name in English', dataIndex: 'nameInEnglish', key: 'nameInEnglish' },
		{ title: 'Department', dataIndex: 'department', key: 'department' },
		{
			title: 'Check-in At',
			dataIndex: 'checkInAt',
			key: 'checkInAt',
			render: (item: any) =>
				!item ? '' : dayjs(item?.slice(0, -1)).format(DATE_FORMATS.DISPLAY_TIME),
		},
		{
			title: 'Check-out At',
			dataIndex: 'checkOutAt',
			key: 'checkOutAt',
			render: (item: any) =>
				!item ? '' : dayjs(item?.slice(0, -1)).format(DATE_FORMATS.DISPLAY_TIME),
		},
		{ title: 'Reason (Check-in)', dataIndex: 'checkinReason', key: 'checkinReason' },
		{ title: 'Reason (Check-out)', dataIndex: 'checkoutReason', key: 'checkoutReason' },
		{ title: 'Team', dataIndex: 'shift', key: 'shift' },
		{
			title: 'Start Working at',
			key: 'startWorkingAt',
			render: (item: any) =>
				!item?.workingHours?.start
					? ''
					: dayjs(item?.workingHours?.start?.slice(0, -1)).format(
							DATE_FORMATS.DISPLAY_TIME,
						),
		},
		{
			title: 'Finish Working at',
			key: 'finishWorkingAt',
			render: (item: any) =>
				!item?.workingHours?.to
					? ''
					: dayjs(item?.workingHours?.to?.slice(0, -1)).format(DATE_FORMATS.DISPLAY_TIME),
		},
	]

	const closeEditModal = () => {
		setEditAttendanceId(null)
	}

	const handleSelectDep = (e: any) => {
		setTableState((prev: any) => ({ ...prev, department: e }))
	}

	const handleClearDep = () => {
		setTableState((prev: any) => ({ ...prev, department: undefined }))
	}

	const onSearch = (value: string | undefined) => {
		setTableState((prev: any) => ({ ...prev, search: value }))
	}

	const handleChangeDatePicker = (e: any) => {
		setTableState((prev: any) => ({
			...prev,
			startDate: e?.[0]?.format(DATE_FORMATS.DISPLAY) || '',
			endDate: e?.[1]?.format(DATE_FORMATS.DISPLAY) || '',
		}))
	}

	const onFetchDepartment = useCallback(async () => {
		try {
			const res: any = await departmentService.fetchListing()
			if (res?.statusCode === 200) {
				setDepartmentOptions(
					(res?.data || []).map((e: any) => ({ label: e?.name, value: e?.id })),
				)
			}
		} catch (error) {
			console.log('error', error)
		}
	}, [])

	const onFetchAttendance = useCallback(
		async (direction: 'reset' | 'next' | 'prev' = 'reset', overridePageSize?: number) => {
			const cursor = cursorPaginationRef.current
			const pageSize = overridePageSize || cursor.pageSize || PAGE_SIZE
			try {
				setLoading(true)
				const nextCurrent =
					direction === 'next'
						? cursor.current + 1
						: direction === 'prev'
							? Math.max(1, cursor.current - 1)
							: 1

				const payload = _.omitBy(
					{
						department_id: tableState.department || undefined,
						staff_id: tableState?.search || undefined,
						start_date: tableState.startDate,
						end_date: tableState.endDate,
						limit: pageSize,
						last_id: direction === 'next' ? cursor.lastDocId : undefined,
						first_id: direction === 'prev' ? cursor.firstDocId : undefined,
					},
					_.isNil,
				)

				const res: any = await attendanceService.fetchListing(payload)
				if (res?.statusCode === 200) {
					const rows = Array.isArray(res?.data) ? res.data : []
					setData(rows)
					const hasNextPage = Boolean(res?.hasNextPage)
					const hasPrevPage = Boolean(res?.hasPrevPage)
					setCursorPagination((prev) => ({
						...prev,
						current: nextCurrent,
						pageSize,
						firstDocId: res?.firstDocId,
						lastDocId: res?.lastDocId,
						hasNextPage,
						hasPrevPage,
						total: (nextCurrent - 1) * pageSize + rows.length + (hasNextPage ? 1 : 0),
					}))
				}
				setLoading(false)
			} catch (error) {
				console.log('error', error)
				setLoading(false)
			}
		},
		[tableState.department, tableState.endDate, tableState.search, tableState.startDate],
	)

	const handlePaginationChange = (pagination: any) => {
		const cursor = cursorPaginationRef.current
		const nextPage = Number(pagination?.current || 1)
		const nextPageSize = Number(pagination?.pageSize || cursor.pageSize || PAGE_SIZE)

		if (nextPageSize !== cursor.pageSize) {
			setCursorPagination((prev) => ({
				...prev,
				current: 1,
				pageSize: nextPageSize,
				firstDocId: undefined,
				lastDocId: undefined,
				hasNextPage: false,
				hasPrevPage: false,
				total: 0,
			}))
			onFetchAttendance('reset', nextPageSize)
			return
		}

		if (nextPage === cursor.current) return

		if (nextPage > cursor.current) {
			if (cursor.hasNextPage) {
				onFetchAttendance('next')
			}
			return
		}

		if (nextPage < cursor.current && cursor.hasPrevPage) {
			onFetchAttendance('prev')
		}
	}

	const handleDownloadExcel = async (exportType: string) => {
		setExportLoading(true)
		try {
			const allRows: any[] = []
			let lastId: string | undefined = undefined
			while (true) {
				const payload = _.omitBy(
					{
						department_id: tableState.department || undefined,
						staff_id: tableState?.search || undefined,
						start_date: tableState.startDate,
						end_date: tableState.endDate,
						limit: 500,
						last_id: lastId,
					},
					_.isNil,
				)
				const res: any = await attendanceService.fetchListing(payload)
				if (res?.statusCode !== 200) break
				const page = Array.isArray(res?.data) ? res.data : []
				allRows.push(...page)
				if (!res?.hasNextPage || !res?.lastDocId) break
				lastId = res.lastDocId
			}

			const excelTableHeader = tableHeader.filter((h) => h.key !== 'action')
			const excelHeader: any = excelTableHeader.map((element) => element.title?.toString())
			let bodyData

			if (exportType === 'group') {
				const groupedData = _.groupBy(allRows, (item) => {
					const date = item.checkInAt
						? dayjs(item.checkInAt.slice(0, -1)).format('YYYY-MM-DD')
						: item.checkOutAt
							? dayjs(item.checkOutAt.slice(0, -1)).format('YYYY-MM-DD')
							: 'no-date'
					return `${item.fullName}-${date}`
				})

				bodyData = Object.values(groupedData).flatMap((records) => {
					// use flatMap
					if (records.length > 2) {
						const recordToUse = { ...records[0] }

						const checkIns = records
							.map((r) =>
								r.checkInAt
									? dayjs(r.checkInAt.slice(0, -1)).format('h:mm:ss a')
									: null,
							)
							.filter(Boolean)
							.join(', ')

						const checkOuts = records
							.map((r) =>
								r.checkOutAt
									? dayjs(r.checkOutAt.slice(0, -1)).format('h:mm:ss a')
									: null,
							)
							.filter(Boolean)
							.join(', ')

						const row = excelTableHeader.map((head: any) => {
							if (head.dataIndex === 'checkInAt') {
								const datePart = records[0].checkInAt
									? dayjs(records[0].checkInAt.slice(0, -1)).format('MM/DD/YYYY')
									: ''
								return datePart ? `${datePart}, ${checkIns}` : checkIns
							}
							if (head.dataIndex === 'checkOutAt') {
								const datePart = records[0].checkOutAt
									? dayjs(records[0].checkOutAt.slice(0, -1)).format('MM/DD/YYYY')
									: ''
								return datePart ? `${datePart}, ${checkOuts}` : checkOuts
							}

							if (head.render) {
								return (
									head.render(
										head.dataIndex ? recordToUse[head.dataIndex] : recordToUse,
									) || ''
								)
							}
							return recordToUse[head.dataIndex] || ''
						})
						return [row] // return array with single merged row
					} else {
						// Not more than 2 records, so return rows for each record as is.
						return records.map((record) => {
							return excelTableHeader.map((head: any) => {
								if (head.render) {
									return (
										head.render(
											head.dataIndex ? record[head.dataIndex] : record,
										) || ''
									)
								}
								return record[head.dataIndex] || ''
							})
						})
					}
				})
			} else {
				bodyData = allRows.map((record) => {
					return excelTableHeader.map((head: any) => {
						if (head.render) {
							return (
								head.render(head.dataIndex ? record[head.dataIndex] : record) || ''
							)
						}
						return record[head.dataIndex] || ''
					})
				})
			}

			const ws = XLSX.utils.aoa_to_sheet([excelHeader, ...bodyData])
			const wb = XLSX.utils.book_new()
			XLSX.utils.book_append_sheet(wb, ws, 'Attendance')
			const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
			const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
			saveAs(blob, 'Attendance Report.xlsx')
		} finally {
			setExportLoading(false)
		}
	}

	const handleMenuClick = (e: any) => {
		handleDownloadExcel(e.key)
	}

	const menuItems = [
		{ key: 'group', label: 'Group By Date' },
		{ key: 'default', label: 'Default (Row by Row)' },
	]

	useEffect(() => {
		onFetchDepartment()
	}, [])

	useEffect(() => {
		setCursorPagination((prev) => ({
			...prev,
			current: 1,
			firstDocId: undefined,
			lastDocId: undefined,
			hasNextPage: false,
			hasPrevPage: false,
			total: 0,
		}))
		onFetchAttendance('reset')
	}, [onFetchAttendance])

	return (
		<>
			<GlobalHeader />

			<PageHeader
				title='Attendance'
				subtitle='Staff attendance records'
				icon={<CalendarOutlined />}
				extra={
					<Dropdown
						menu={{ items: menuItems, onClick: handleMenuClick }}
						disabled={exportLoading}
					>
						<Button icon={<FileExcelTwoTone />} loading={exportLoading}>
							Export <DownOutlined />
						</Button>
					</Dropdown>
				}
			/>

			{/* ── Filter Bar ──────────────────────────────────────────────── */}
			<div style={{ padding: '12px 24px 0' }}>
				<Card
					size='small'
					style={{
						borderRadius: 8,
						boxShadow: theme.cardShadow,
						backgroundColor: isDarkMode ? theme.subBg : '#fff',
						border: `1px solid ${theme.border}`
					}}
					styles={{ body: { padding: '12px 16px' } }}
				>
					<Row gutter={[12, 8]} align='middle'>
						<Col xs={24} sm={12} md={6}>
							<Input.Search
								placeholder='Search by Staff ID'
								allowClear
								type='number'
								onSearch={onSearch}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Select
								allowClear
								showSearch
								style={{ width: '100%' }}
								placeholder='Search by Department'
								onSelect={handleSelectDep}
								onClear={handleClearDep}
								value={tableState?.department}
								options={departmentOptions}
							/>
						</Col>
						<Col xs={24} sm={24} md={12}>
							<RangePicker
								allowEmpty={[true, true]}
								size='middle'
								format={DATE_FORMATS.DISPLAY}
								onChange={handleChangeDatePicker}
								style={{ width: '100%' }}
								value={[
									dayjs(tableState?.startDate || '', DATE_FORMATS.DISPLAY),
									dayjs(tableState?.endDate || '', DATE_FORMATS.DISPLAY),
								]}
							/>
						</Col>
					</Row>
				</Card>
			</div>

			{/* ── Table ───────────────────────────────────────────────────── */}
			<div style={{ padding: '8px 24px 24px' }}>
				<Card
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: 0 } }}
				>
					<AntTable
						columns={tableHeader}
						dataSource={data}
						rowKey={(record) => record?.id}
						loading={loading}
						serverPagination
						pageSizeOptions={['10', '20', '50', '100']}
						paginationState={{
							current: cursorPagination.current,
							pageSize: cursorPagination.pageSize,
							total: cursorPagination.total,
						}}
						onPaginationChange={handlePaginationChange}
					/>
				</Card>
			</div>

			<Modal
				title={null}
				open={!!editAttendanceId}
				onCancel={closeEditModal}
				footer={null}
				destroyOnHidden
				width={900}
			>
				{editAttendanceId && (
					<AttendanceEditorPage
						attendanceIdOverride={editAttendanceId}
						embedded
						onClose={closeEditModal}
						onSuccess={() => {
							onFetchAttendance('reset')
						}}
					/>
				)}
			</Modal>
		</>
	)
}

export default AttendancePage
